/**
 * screens/settings/domainsPanel.tsx — Domains (SKPLATFORM-UICC-001 CH-U1).
 *
 * A bound domain is the ONLY thing that turns a rider's browser request into
 * this operator's org (SKPLATFORM-CC-002 CH-1). That makes this panel a routing
 * control, not a profile field, and it shapes three decisions here:
 *
 *   · **No delete exists.** Unbinding stamps `deactivated_at`; the row stays and
 *     is shown struck-through under "All". The API has no delete either — the
 *     history of which operator owned a domain is evidence, and a console that
 *     could erase it would be the only place that ever did.
 *   · **A conflict never names the owning org.** "This domain is already in
 *     use." and nothing more. Naming the holder would leak one tenant's
 *     configuration to another, which is why the API refuses to send it.
 *   · **org_admin only, and read-only once the org is deactivated**, because a
 *     binding that cannot resolve is worse than no binding: it reads as working
 *     configuration.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { View, Text } from 'react-native';

import { useTheme } from '../../context/themeContext';
import { useToast } from '../../components/toast';
import { Panel, DataTable, StatusBadge } from '../../components/platform/primitives';
import { PButton, PTextInput, PSelect, PEmpty } from '../../components/platform/controls';
import { FS, FW } from '../../utils/platformTokens';
import { hostValidationError, normaliseHost } from '../../validations/hostname';
import { formatMembershipWindow } from '../../utils/membershipWindow';
import {
  listOrgHosts,
  bindOrgHost,
  deactivateOrgHost,
} from '../../services/platform/platformApi';
import type { OrgHost } from '../../schema/platform/types';
import { getApiErrorMessage } from '../../utils/errorHandler';

type HostFilter = 'active' | 'all';

const DomainsPanel: React.FC<{
  orgId: string;
  orgSlug?: string;
  canEdit: boolean;
  /** Resolution is suspended for a deactivated org — the panel goes read-only. */
  orgActive?: boolean;
}> = ({ orgId, orgSlug, canEdit, orgActive = true }) => {
  const { theme } = useTheme();
  const c = theme.colors;
  const toast = useToast();

  const [hosts, setHosts] = useState<OrgHost[]>([]);
  const [filter, setFilter] = useState<HostFilter>('active');
  const [loading, setLoading] = useState(true);
  const [input, setInput] = useState('');
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  // Inline confirm, not a browser dialog and not a modal — CH-U1. The row
  // expands in place so the domain being unbound stays on screen while the
  // person reads the consequence.
  const [confirmId, setConfirmId] = useState<string | null>(null);

  const editable = canEdit && orgActive;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setHosts(await listOrgHosts(orgId, filter === 'all'));
    } catch {
      // The panel stays mounted and empty rather than blanking the org screen:
      // a domains outage should not take the profile down with it.
      setHosts([]);
    } finally {
      setLoading(false);
    }
  }, [orgId, filter]);

  useEffect(() => { load(); }, [load]);

  const add = async () => {
    const err = hostValidationError(input);
    if (err) { setFieldError(err); return; }

    setSaving(true);
    setFieldError(null);
    try {
      await bindOrgHost(orgId, normaliseHost(input));
      setInput('');
      toast.success('Domain added.');
      await load();
    } catch (e: any) {
      // 409 is the duplicate-active-host conflict. It surfaces INLINE under the
      // field, not as a toast, because it is a problem with the value they
      // typed — and the message deliberately says nothing about who holds it.
      const message =
        e?.response?.status === 409
          ? 'This domain is already in use.'
          : getApiErrorMessage(e, 'Could not add that domain.');
      setFieldError(message);
    } finally {
      setSaving(false);
    }
  };

  const deactivate = async (h: OrgHost) => {
    try {
      await deactivateOrgHost(orgId, h.id);
      toast.success('Domain deactivated. Riders can no longer reach you through it.');
      setConfirmId(null);
      await load();
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Could not deactivate that domain.'));
      setConfirmId(null);
    }
  };

  return (
    <Panel
      title="Domains"
      sub="The addresses riders reach this operator on."
      actions={
        <PSelect
          size="sm"
          width={130}
          value={filter}
          options={[{ value: 'active', label: 'Active' }, { value: 'all', label: 'All' }]}
          onChange={(v) => setFilter(v as HostFilter)}
        />
      }
    >
      {/* The slug participates in resolution too (GET /v1/internal/resolve?slug=),
          so it belongs here rather than buried in the profile — but it is not
          editable from this panel. */}
      {!!orgSlug && (
        <View style={{ marginBottom: 14 }}>
          <Text style={{ fontSize: FS.sm, color: c.ink3 }}>Slug</Text>
          <Text style={{ fontFamily: 'monospace', color: c.ink, fontSize: FS.md }}>{orgSlug}</Text>
          <Text style={{ fontSize: FS.sm, color: c.ink3 }}>Used as internal reference</Text>
        </View>
      )}

      {!orgActive && (
        <Text style={{ fontSize: FS.md, color: c.ink3, marginBottom: 12 }}>
          This organization is deactivated — domain resolution is suspended and these
          bindings cannot be changed.
        </Text>
      )}

      {editable && (
        <View style={{ marginBottom: 16 }}>
          <View style={{ flexDirection: 'row', alignItems: 'flex-start', gap: 10 }}>
            <View style={{ flex: 1 }}>
              <PTextInput
                value={input}
                onChangeText={(v) => { setInput(v); if (fieldError) setFieldError(null); }}
                placeholder="taxis.urbanway.com"
                mono
                error={fieldError ?? undefined}
                label="Add domain"
              />
            </View>
            <View style={{ paddingTop: 24 }}>
              <PButton kind="accent" onPress={add} disabled={saving}>
                {saving ? 'Adding…' : 'Add'}
              </PButton>
            </View>
          </View>
        </View>
      )}

      {loading ? (
        <Text style={{ color: c.ink3 }}>Loading domains…</Text>
      ) : hosts.length === 0 ? (
        <PEmpty icon="home" title="No domains bound">
          Riders reach this operator through a bound domain — add one to enable the
          customer app.
        </PEmpty>
      ) : (
        <DataTable<OrgHost>
          minWidth={620}
          keyExtractor={(h) => h.id}
          rows={hosts}
          columns={[
            {
              key: 'host', header: 'Domain', width: 300,
              render: (h) => (
                <View>
                  <Text
                    style={{
                      fontFamily: 'monospace',
                      color: h.status === 'active' ? c.ink : c.ink3,
                      fontSize: FS.md,
                      // Struck-through, VISIBLE — a deactivated binding is
                      // history the operator should be able to see, not
                      // something hidden as if it never happened.
                      textDecorationLine: h.status === 'active' ? 'none' : 'line-through',
                    }}
                  >
                    {h.host}
                  </Text>
                  {h.status !== 'active' && (
                    <Text style={{ fontSize: FS.sm, color: c.ink3 }}>
                      {formatMembershipWindow({ joinedAt: h.created_at, deactivatedAt: h.deactivated_at })
                        .replace('joined', 'added')}
                    </Text>
                  )}
                </View>
              ),
            },
            {
              key: 'added', header: 'Added', width: 130,
              render: (h) => (
                <Text style={{ color: c.ink3, fontSize: FS.md }}>
                  {new Date(h.created_at).toLocaleDateString()}
                </Text>
              ),
            },
            { key: 'status', header: 'Status', width: 120, render: (h) => <StatusBadge status={h.status} /> },
            {
              key: 'actions', header: '', align: 'right',
              render: (h) => {
                if (!editable || h.status !== 'active') return null;
                if (confirmId === h.id) {
                  return (
                    <View style={{ alignItems: 'flex-end', gap: 6 }}>
                      <Text style={{ fontSize: FS.sm, color: c.ink2, maxWidth: 320, textAlign: 'right' }}>
                        Deactivate this domain? Riders using it will no longer reach this operator.
                      </Text>
                      <View style={{ flexDirection: 'row', gap: 8 }}>
                        <PButton size="sm" kind="ghost" onPress={() => setConfirmId(null)}>Cancel</PButton>
                        <PButton size="sm" kind="danger" onPress={() => deactivate(h)}>Deactivate</PButton>
                      </View>
                    </View>
                  );
                }
                return (
                  <PButton size="sm" kind="ghost" onPress={() => setConfirmId(h.id)}>
                    Deactivate
                  </PButton>
                );
              },
            },
          ]}
        />
      )}

      <Text style={{ fontSize: FS.sm, color: c.ink3, marginTop: 12, fontWeight: FW.regular }}>
        Domains are never deleted. Deactivating one keeps the record of when it was bound.
      </Text>
    </Panel>
  );
};

export default DomainsPanel;
